import React, { createContext, useState, } from "react"
import { appInsights } from "../AppInsightsService.tsx";
import axios from 'axios';
import { IResourceToken } from "../DataService/DataServiceInterfaces.ts";
import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { Config } from "../config.ts";
import { GLNavClient } from "./CustomNavigationClient.ts";
import { v4 as uuid } from 'uuid';
import { EventHook, EventHookManager, IEventArgsBase } from "../../Utilities/EventHookManager.ts";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

const doLogging = false;
const AddTimeStamp = (str: string) => {
    return new Date().toISOString() + " | " + str;
}
/**
 * Represents the authentication context.
 */
export interface IAuthContext {
    // AcquireResourceToken: () => Promise<IResourceToken | undefined>;
    // LoginUser: () => void;
    isLoggedIn: boolean;
    userId?: string;
}
export interface IUserChanged extends IEventArgsBase {
    userId: string | undefined;
}


export enum TokenScope {
    UserReadWrite = "user.readwrite",
    UserReadOnly = "user.readonly",
    SharedReadOnly = "shared.readonly",
    idToken = "idToken"
}




export interface IAuthServices {
    AcquireIdToken: () => Promise<Object | undefined>;
    AcquireResourceToken: (type?: TokenScope) => Promise<IResourceToken | AuthenticationResult | undefined>;
    ChangePassword: () => Promise<void>;
    LoginUser: () => Promise<void>;
    LogOutUser: () => Promise<void>;
    DeleteAccount: () => Promise<void>;
    IsTestAccount: () => boolean;
    userChanged: EventHook<IUserChanged>;
    userDeleted: EventHook<IUserChanged>;
    userId?: string;
    IsLoggedIn: () => boolean;
}



//
// Create React Authentication Context and save the setters so that the AuthServices can update the context.
// The AuthServices now have no direct dependency on React!
//
export const GLAuthContext = createContext<IAuthContext | undefined>(undefined);
var setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>> | undefined = undefined;
var setUserId: React.Dispatch<React.SetStateAction<string | undefined>> | undefined = undefined;
var globalUserId: string | undefined = undefined;
var cachedAccessTokenMap = new Map<TokenScope, any>();
const UserIdKey = "AuthServices|UserId";
/**
 * Authentication service provider component.
 * @param props - The component props with children to render.
 * @returns The rendered component with GLAuthContext available.
 */
export function AuthServiceProvider(props: { children?: React.ReactNode }) {

    const [isLoggedIn, setIsLoggedInFn] = useState(globalUserId ? true : false);
    setIsLoggedIn = setIsLoggedInFn;

    const [userId, setUserIdFn] = useState<string | undefined>(globalUserId);
    setUserId = setUserIdFn;


    const authContext = {
        isLoggedIn: isLoggedIn,
        userId: userId
    };

    return (
        <GLAuthContext.Provider value={authContext}>
            {props.children}
        </GLAuthContext.Provider>

    );
}





var userDeleted = new EventHookManager<IUserChanged>();
var userChanged = new EventHookManager<IUserChanged>();
var userChangedInitialFire = false;
var authServices: IAuthServices | undefined = undefined;
var getAuthServicesPromise: Promise<IAuthServices> | undefined = undefined;



export async function GetAuthServices() {
    if (getAuthServicesPromise)
        return getAuthServicesPromise!;



    getAuthServicesPromise = (async () => {
        if (!authServices) {
            doLogging && console.log("Getting auth services");
            var pca = await GLNavClient.GetPCA();
            doLogging && console.log("Got PCA", pca);



            const loginRequest = {
                scopes: ["https://GrowthLenses.onmicrosoft.com/API/user.readwrite"] // Define the scopes your app needs
            };

            var msalCookies: string[] = [];
            var msalInteractionPromise: Promise<void> | undefined = undefined;
            const guardMsalInteraction = async (fn: () => Promise<any>) => {

                let myCookie = uuid();
                msalCookies.push(myCookie);

                while (true) {

                    // wait for the previous interaction to complete
                    //
                    msalInteractionPromise && await msalInteractionPromise;

                    //
                    // Check to see if we are next in the queue
                    //
                    let currentCookie = msalCookies.pop();
                    if (!currentCookie || currentCookie === myCookie)
                        break;
                    else
                        msalCookies.push(currentCookie);
                }

                //
                // Run the function
                //
                msalInteractionPromise = fn();
                await msalInteractionPromise;
            }

            /**
             * Updates the user information from the authentication token.
             * @param authToken - The authentication token.
             */
            const updateUser = (account?: AccountInfo): void => {


                doLogging && console.log("Updating user", account);
                const id = account?.localAccountId;
                var fireEvent: () => void;
                if (id !== globalUserId) {

                    cachedAccessTokenMap.clear();

                    if (id) {

                        appInsights.trackTrace({ message: "AuthServices | UpdateUser | Setting user as logged in", severityLevel: SeverityLevel.Verbose, properties: { id: id } });
                        doLogging && console.log("Setting user as logged in", id);
                        setIsLoggedIn && setIsLoggedIn(true);


                        appInsights.setAuthenticatedUserContext(id, id, true);
                        pca.setActiveAccount(account);
                        var infoString = JSON.stringify(account);

                        appInsights.trackTrace({ message: "AuthServices | UpdateUser | Set account info", severityLevel: SeverityLevel.Verbose, properties: { infoString: infoString } });
                        doLogging && console.log("Set account info", infoString);
                        fireEvent = () => appInsights.fireUserLoggedIn(id);

                    } else {

                        appInsights.trackTrace({ message: "AuthServices | UpdateUser | Setting user as logged out", severityLevel: SeverityLevel.Verbose, properties: { id: globalUserId } });
                        doLogging && console.log("Setting user as logged out");
                        setIsLoggedIn && setIsLoggedIn(false);



                        appInsights.trackTrace({ message: "AuthServices | UpdateUser | Removing account info", severityLevel: SeverityLevel.Verbose });
                        doLogging && console.log("Removing account info");
                        appInsights.clearAuthenticatedUserContext();
                        pca.setActiveAccount(null);
                        fireEvent = () => appInsights.fireUserLoggedOut(globalUserId ?? '');
                    }

                    appInsights.trackTrace({ message: "AuthServices | UpdateUser | Setting user id", severityLevel: SeverityLevel.Verbose, properties: { id: id } });
                    doLogging && console.log("Setting user id", id);
                    setUserId && setUserId(id);
                    globalUserId = id;
                    let cachedId = window.localStorage.getItem(UserIdKey);
                    if (cachedId !== globalUserId || !userChangedInitialFire) {
                        userChangedInitialFire = true;
                        userChanged.Fire({ source: undefined, userId: globalUserId });
                    }
                    window.localStorage.setItem(UserIdKey, globalUserId ?? "");
                    fireEvent();
                }
            }




            /**
             * Logs in the user.
             */
            const LoginUser = async (): Promise<void> => {


                //
                // First check to see if we already have an account
                // 
                var temp = pca.getAllAccounts();

                appInsights.trackTrace({ message: "AuthServices | LoginUser | Got accounts", severityLevel: SeverityLevel.Verbose, properties: { count: temp.length } });
                doLogging && console.log("Got accounts...", temp);

                if (temp.length > 0) {

                    appInsights.trackTrace({ message: "AuthServices | LoginUser | Found account, updating user", severityLevel: SeverityLevel.Verbose, properties: { account: temp[0] } });
                    doLogging && console.log("Found account, updating user");
                    updateUser(temp[0]);
                    return;

                }
                else {

                    appInsights.trackTrace({ message: "AuthServices | LoginUser | No account found, logging in", severityLevel: SeverityLevel.Verbose });
                    doLogging && console.log("No account found, logging in");


                    //
                    // Check to see if we are already logging in
                    //
                    if (msalInteractionPromise) {
                        appInsights.trackTrace({ message: "AuthServices | LoginUser | Already logging in", severityLevel: SeverityLevel.Verbose });
                        doLogging && console.log("Already logging in");
                        return msalInteractionPromise;
                    }

                    var account = pca.getActiveAccount();
                    if (account) {
                        appInsights.trackTrace({ message: "AuthServices | LoginUser | Already logged in", severityLevel: SeverityLevel.Information, properties: { account: account } });
                        doLogging && console.log("Already logged in", account);
                        return;
                    }

                    const doLogin = async () => {
                        try {
                            var authToken: AuthenticationResult;
                            if (window.location !== window.parent.location) {
                                // The page is in an iFrames
                                appInsights.trackTrace({ message: "AuthServices | LoginUser | Logging in with popup", severityLevel: SeverityLevel.Verbose });
                                doLogging && console.log("Logging in with popup");
                                authToken = await pca.loginPopup(loginRequest);
                                updateUser(authToken.account);
                            }
                            else {
                                // The page is not in an iFrame
                                appInsights.trackTrace({ message: "AuthServices | LoginUser | Logging in with redirect", severityLevel: SeverityLevel.Verbose });
                                doLogging && console.log(AddTimeStamp("Logging in with redirect"));
                                await guardMsalInteraction(async () => { await pca.loginRedirect(loginRequest) })

                                // await new Promise(resolve => setTimeout(resolve, 1000));
                                await GLNavClient.externalNavigatePromise;
                                var temp = pca.getAllAccounts();

                                appInsights.trackTrace({ message: "AuthServices | LoginUser | Got accounts", severityLevel: SeverityLevel.Verbose, properties: { count: temp.length } });
                                doLogging && console.log(AddTimeStamp("Got accounts..."), temp.length);
                                var account = pca.getActiveAccount();

                                if (!account) {
                                    appInsights.trackTrace({ message: "AuthServices | LoginUser | Failed to login", severityLevel: SeverityLevel.Error });
                                    doLogging && console.error("Failed to login");
                                    LogOutUser();
                                }
                                else {
                                    appInsights.trackTrace({ message: "AuthServices | LoginUser | Logged in with redirect", severityLevel: SeverityLevel.Information, properties: { account: account } });
                                    doLogging && console.log("Logged in with redirect");
                                }
                            }

                        }
                        catch (e) {
                            updateUser();
                            throw e;
                        }
                    };
                    await guardMsalInteraction(doLogin);
                    return msalInteractionPromise;
                }
            };





            /**
             * Logs out the user.
             */
            const LogOutUser = async (): Promise<void> => {

                appInsights.trackEvent({ name: "LogOutUser" });
                appInsights.trackTrace({ message: "AuthServices | LogOutUser", severityLevel: SeverityLevel.Information, properties: { userId: globalUserId } });
                var config = await GLNavClient.GetConfiguration();

                updateUser();
                await pca.logoutRedirect({
                    account: pca.getActiveAccount(),
                    onRedirectNavigate: (url) => {
                        GLNavClient.navigateExternal(url, {});
                        return false;
                    },
                    postLogoutRedirectUri: config.auth.redirectUri
                });
                pca.clearCache();
                GLNavClient.ClearCookies();
                await new Promise(resolve => setTimeout(resolve, 1000));
            }





            const ChangePassword = async (): Promise<void> => {
                appInsights.trackEvent({ name: "ChangePassword" });
                appInsights.trackTrace({ message: "AuthServices | ChangePassword", severityLevel: SeverityLevel.Information, properties: { userId: globalUserId } });
                await pca.loginRedirect(GLNavClient.PassWordResetRequest);
            }





            /**
             * Acquires the id token.
             * @returns The id token.
             */
            const AcquireIdToken = async (): Promise<Object | undefined> => {

                // 
                // Ensure we are initialized
                //
                await guardMsalInteraction(() => Promise.resolve());
                const account = pca.getActiveAccount();
                var retVal: Object | undefined = undefined;
                if (account) {
                    appInsights.trackTrace({ message: "AuthServices | AcquireIdToken | Acquiring token", severityLevel: SeverityLevel.Verbose, properties: { account: account } });
                    doLogging && console.log("Acquiring resource token for account", account);
                    let val = await AcquireResourceToken(TokenScope.idToken);
                    let authResult = val as AuthenticationResult;
                    retVal = authResult.idTokenClaims;
                }
                else {
                    appInsights.trackTrace({ message: "AuthServices | AcquireIdToken | No account found", severityLevel: SeverityLevel.Error });
                    doLogging && console.error("No account found");
                    LogOutUser();
                }
                return retVal;
            }




            /**
             * Acquires the resource token.
             * @returns The resource token.
             */
            const AcquireResourceToken = async (scope: TokenScope = TokenScope.UserReadWrite): Promise<IResourceToken | AuthenticationResult | undefined> => {

                await guardMsalInteraction(() => Promise.resolve());
                const account = pca.getActiveAccount();
                const instance = pca;

                appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken", severityLevel: SeverityLevel.Verbose, properties: { scope: scope, account: account } });
                doLogging && console.log("Getting active account", account);
                if (account) {

                    const cachedAccessToken = cachedAccessTokenMap.get(scope);

                    var isCachedTokenValid = cachedAccessToken !== undefined;
                    if (cachedAccessToken) {
                        //
                        // Check to see if the token is still valid
                        //
                        appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Checking cached token", severityLevel: SeverityLevel.Verbose, properties: { cachedAccessToken: cachedAccessToken } });
                        doLogging && console.log("Checking cached token", cachedAccessToken);

                        var currentTime = new Date();
                        currentTime.setMinutes(currentTime.getMinutes() - 3);
                        if (cachedAccessToken.resourcePermissions && cachedAccessToken.resourcePermissions.forEach) {
                            cachedAccessToken.resourcePermissions.forEach((element: any) => {
                                if (element.expiresUtc < currentTime) {
                                    isCachedTokenValid = false;
                                }
                            });
                        }
                        else
                            isCachedTokenValid = false;

                        appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Cached token is" + isCachedTokenValid ? "valid" : "invalid", severityLevel: SeverityLevel.Verbose, properties: { isCachedTokenValid: isCachedTokenValid } })
                    }

                    if (!isCachedTokenValid) {

                        appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Acquiring new resource token", severityLevel: SeverityLevel.Verbose });
                        doLogging && console.log("Acquiring new resource token for account", account);


                        var authToken: AuthenticationResult;
                        await guardMsalInteraction(
                            (async () => {

                                appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Acquiring token silently", severityLevel: SeverityLevel.Verbose, properties: { redirectUri: (await GLNavClient.GetConfiguration()).auth.redirectUri } });
                                doLogging && console.log("Acquiring token silently", (await GLNavClient.GetConfiguration()).auth.redirectUri);
                                let scopes: string[] = [];
                                switch (scope) {
                                    case TokenScope.UserReadWrite:
                                        scopes = [
                                            "https://GrowthLenses.onmicrosoft.com/API/user.readwrite",
                                        ];
                                        break;
                                    case TokenScope.SharedReadOnly:
                                        scopes = [
                                            "https://GrowthLenses.onmicrosoft.com/API/shared.readonly"
                                        ];
                                        break;
                                    case TokenScope.UserReadOnly:
                                        scopes = [
                                            "https://GrowthLenses.onmicrosoft.com/API/user.readonly"
                                        ];
                                        break;
                                    case TokenScope.idToken:
                                        scopes = [
                                            "https://GrowthLenses.onmicrosoft.com/API/user.readwrite"
                                        ];
                                        break;
                                    default:
                                        throw new Error("Invalid scope");
                                }
                                try {
                                    authToken = await instance.acquireTokenSilent({
                                        scopes,
                                        account
                                    });
                                }
                                catch (e) {
                                    if ((e as any).errorCode === 'interaction_required') {
                                        console.log("!!! Interaction required !!!");
                                        LogOutUser();
                                    }
                                    else {
                                        appInsights.trackTrace({
                                            message: "AuthServices | AcquireResourceToken | Failed to acquire token silently",
                                            severityLevel: SeverityLevel.Error
                                        });
                                        appInsights.trackException({ error: e as Error });
                                        console.error("Failed to acquire token silently", e);
                                        LogOutUser();

                                        throw e;
                                    }

                                }
                            })
                        );

                        // We just set the auth token, so we know it's not null
                        //
                        authToken = authToken!;


                        if (scope === TokenScope.idToken) {
                            appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Returning id token and skipping caching", severityLevel: SeverityLevel.Verbose, properties: { account: authToken.account } });
                            return authToken;

                        }
                        else {

                            // Update the user id locally and in App Insights
                            //            
                            appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Updating user", severityLevel: SeverityLevel.Verbose, properties: { account: authToken.account } });
                            doLogging && console.log("Updating user", authToken.account);
                            updateUser(authToken.account);

                            //
                            // Get the resource token
                            //
                            appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Getting resource token", severityLevel: SeverityLevel.Verbose });
                            doLogging && console.log("Getting resource token");
                            var response = await axios.get(Config.ResourceUrl, {
                                headers: {
                                    'Authorization': `Bearer ${authToken.accessToken}`
                                }
                            });

                            var retVal = response.data;

                            if (!GLNavClient.isNative) {
                                appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Not native, checking URL and redirecting if necessary", severityLevel: SeverityLevel.Verbose });
                                doLogging && console.log("Not native, checking URL and redirecting if necessary");
                                var targetAppUrl = Config.AppUrl ?? retVal.appUrl;

                                if (!window.location.href.startsWith(targetAppUrl))
                                    window.location.assign(targetAppUrl);
                            }


                            retVal.resourcePermissions.forEach((element: any) => {
                                element.expiresUtc = new Date(element.expiresUtc);
                            });

                            appInsights.trackTrace({ message: "AuthServices | AcquireResourceToken | Caching resource token", severityLevel: SeverityLevel.Verbose, properties: { scope: scope, retVal: retVal } });
                            doLogging && console.log("Resource token acquired, caching it", retVal);
                            cachedAccessTokenMap.set(scope, retVal);
                        }
                    }

                    return cachedAccessTokenMap.get(scope);
                }
            }

            const IsTestAccount = () => {
                switch (globalUserId) {
                    case 'ab68f5c9-0b28-462a-896e-72170fb87d8c':
                        return true;
                    default:
                        return false;
                }
            }


            const DeleteAccount = async () => {
                userDeleted.Fire({ source: undefined, userId: globalUserId });
            }

            const IsLoggedIn = () => {
                return globalUserId !== undefined && globalUserId !== null && globalUserId !== '';
            }

            authServices = {
                AcquireIdToken,
                AcquireResourceToken,
                ChangePassword,
                LoginUser,
                LogOutUser,
                DeleteAccount,
                IsTestAccount,
                userDeleted: userDeleted.eventHook,
                userChanged: userChanged.eventHook,
                userId: globalUserId,
                IsLoggedIn
            };
        }

        // // Kick off the login process
        // //
        // authServices.LoginUser();

        return authServices;
    })();
    return await getAuthServicesPromise;
}

GetAuthServices(); // Kick off the initialization
